/**
 * Tiny Web Speech API wrapper. Returns a stable object with start/stop and
 * the most-recent interim+final transcript. Graceful no-op on unsupported
 * browsers (iOS Safari <14.5, some embedded webviews).
 */
import { useEffect, useRef, useState, useCallback } from 'react';

const SpeechRecognition = typeof window !== 'undefined'
  ? (window.SpeechRecognition || window.webkitSpeechRecognition)
  : null;

export const voiceSupported = !!SpeechRecognition;

export default function useVoiceInput({ onFinal, lang = 'en-US' } = {}) {
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState(null);
  const recRef = useRef(null);
  const finalRef = useRef('');
  const onFinalRef = useRef(onFinal);

  useEffect(() => { onFinalRef.current = onFinal; }, [onFinal]);

  const stop = useCallback(() => {
    try { recRef.current?.stop(); } catch {}
  }, []);

  const start = useCallback(() => {
    setError(null);
    setTranscript('');
    finalRef.current = '';
    if (!SpeechRecognition) {
      setError('Voice input is not supported on this browser');
      return;
    }
    try {
      const rec = new SpeechRecognition();
      rec.lang = lang;
      rec.interimResults = true;
      rec.continuous = false;
      rec.maxAlternatives = 1;
      rec.onresult = (e) => {
        let interim = '';
        for (let i = e.resultIndex; i < e.results.length; i++) {
          const r = e.results[i];
          if (r.isFinal) finalRef.current += r[0].transcript;
          else interim += r[0].transcript;
        }
        setTranscript((finalRef.current + interim).trim());
      };
      rec.onerror = (e) => {
        const msg = e.error === 'not-allowed'
          ? 'Microphone permission denied'
          : `Mic error: ${e.error || 'unknown'}`;
        setError(msg);
        setListening(false);
      };
      rec.onend = () => {
        setListening(false);
        const txt = finalRef.current.trim();
        if (txt && onFinalRef.current) onFinalRef.current(txt);
      };
      recRef.current = rec;
      rec.start();
      setListening(true);
    } catch (e) {
      setError(String(e.message || e));
      setListening(false);
    }
  }, [lang]);

  useEffect(() => () => { try { recRef.current?.abort(); } catch {} }, []);

  return { listening, transcript, error, supported: voiceSupported, start, stop };
}
